import { useTranslation } from "react-i18next";

import { useMask } from "@react-input/mask";
import { FormikValues } from "formik";

import { FormikFieldProps, OptionalOrRequired } from "../FormikField.types";
import { getFieldPath, getFieldValue } from "../FormikField.utils";
import { FormikTextField } from "../FormikTextField";
import { formatIban, normalizeIban } from "./FormikIbanField.utils";

type FormikIbanFieldProps<T extends FormikValues> = FormikFieldProps<T> &
  OptionalOrRequired & {
    disabled?: boolean;
  };

export function FormikIbanField<T extends FormikValues>({ label, ...fieldProps }: FormikIbanFieldProps<T>) {
  const { t } = useTranslation();

  const maskRef = useMask({
    mask: "??## ____ ____ ____ ____ ____ ____ ____ __",
    replacement: { "?": /[A-Za-z]/, "#": /[0-9]/, _: /[0-9A-Za-z]/ },
  });

  const fullPath = getFieldPath(fieldProps.path, fieldProps.fieldName);
  const canonicalValue = (getFieldValue(fieldProps.formik, fullPath) ?? "") as string;
  const displayValue = formatIban(canonicalValue);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const canonical = normalizeIban(e.target.value);
    void fieldProps.formik.setFieldValue(fullPath, canonical || null);
  };

  return (
    <FormikTextField
      {...fieldProps}
      inputRef={maskRef}
      label={label ?? t("common.iban.label")}
      placeholder={t("common.iban.placeholder")}
      onChange={handleChange}
      value={displayValue}
    />
  );
}
